import { Command } from './Command';
import { updateOverlapStates } from '../components/Editor/utils/overlapDetection';
import { GARDEN_OBJECT_TYPES } from '../constants/gardenObjectTypes';
import { isLinkableParent, syncLinkedGates } from '../components/Editor/tools/gateLinkUtils';

/**
 * TransformObjectCommand - Handles object transformations
 * Used for: Move, Scale, Rotate operations
 *
 * Note: This is used for continuous operations like dragging/scaling.
 * Capture state on mouse:down, execute command on mouse:up.
 *
 * @example
 * const oldTransform = { left: 100, top: 100, scaleX: 1, scaleY: 1, angle: 0 };
 * const newTransform = { left: 200, top: 150, scaleX: 1.5, scaleY: 1.5, angle: 45 };
 * const command = new TransformObjectCommand(rect, oldTransform, newTransform);
 * commandStack.execute(command);
 */
export class TransformObjectCommand extends Command {
  /**
   * @param {Object} object - Fabric.js object to transform
   * @param {Object} oldTransform - Previous transform state (left, top, scaleX, scaleY, angle)
   * @param {Object} newTransform - New transform state
   * @param {Object|null} linkState - Gate link change during the transform (#611):
   *   { before: link|null, after: link|null } — restored alongside the transform
   */
  constructor(object, oldTransform, newTransform, linkState = null) {
    super();
    this.object = object;
    this.oldTransform = { ...oldTransform };
    this.newTransform = { ...newTransform };
    this.linkState = linkState
      ? {
          before: linkState.before ? { ...linkState.before } : null,
          after: linkState.after ? { ...linkState.after } : null,
        }
      : null;
  }

  _applyLink(link) {
    const data = { ...this.object.data };
    if (link) {
      data.link = { ...link };
    } else {
      delete data.link;
    }
    this.object.data = data;
  }

  _afterApply() {
    this.object.setCoords();

    // Linked gates derive their position from the parent — keep them in sync,
    // also through undo/redo (#611)
    if (this.object.canvas && isLinkableParent(this.object)) {
      syncLinkedGates(this.object.canvas, this.object);
    }

    if (this.object.canvas) {
      this.object.canvas.renderAll();
    }
    // Fire modified event so properties panel updates
    this.object.fire('modified');

    // Update overlap detection if this is a plant
    if (this.object.objectType === GARDEN_OBJECT_TYPES.PLANT && this.object.canvas) {
      updateOverlapStates(this.object.canvas);
    }
  }

  execute() {
    this.object.set(this.newTransform);
    if (this.linkState) {
      this._applyLink(this.linkState.after);
    }
    this._afterApply();
  }

  undo() {
    this.object.set(this.oldTransform);
    if (this.linkState) {
      this._applyLink(this.linkState.before);
    }
    this._afterApply();
  }

  getDescription() {
    const type = this.object.data?.type || this.object.type;
    return `Transform ${type}`;
  }
}
